import { Badge, Button, Card, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { resolveAccountDomain } from '@econtractor/types';
import conf from 'conf/conf';
import './TariffItem.css';
import SolarIcon from 'components/icons/SolarIcon';
import { isPlCountry } from '../helper/tariffHelper';

export type TariffKey = 'starter' | 'professional' | 'enterprise';

export interface TariffPlan {
    id: number | 'enterprise';
    key: TariffKey;
    priceUsd: number;
    maxObjects: number | null;
    maxStorageGb: number | null;
    featured?: boolean;
    pricePeriod?: 'year' | 'purchase';
}

const PLAN_ICONS: Record<TariffKey, string> = {
    starter: 'bi-rocket-takeoff',
    professional: 'bi-briefcase',
    enterprise: 'bi-building',
};

interface Props {
    plan: TariffPlan;
    isCurrent: boolean;
    formattedPrice: string;
    accountUuid: string;
    accountDomain?: string;
    currentTariff: number;
    currentExpireAt: number;
    locale: string;
    onEnterpriseRequest?: () => void;
    invoiceByDetailsEnabled: boolean;
    onInvoiceByDetailsToggle: (enabled: boolean) => void;
    onInvoiceRequest?: (plan: TariffPlan) => void;
    onApplicationRequest?: (plan: TariffPlan) => void;
}

const TariffItem = ({
    plan,
    isCurrent,
    formattedPrice,
    accountUuid,
    accountDomain,
    currentTariff,
    currentExpireAt,
    locale,
    onEnterpriseRequest,
    invoiceByDetailsEnabled,
    onInvoiceByDetailsToggle,
    onInvoiceRequest,
    onApplicationRequest,
}: Props) => {
    const { t } = useTranslation('Tariff');
    const isEnterprise = plan.key === 'enterprise';
    const isPl = isPlCountry();
    const isPayableTariff = typeof plan.id === 'number';
    const isDowngrade = Boolean(
        isPayableTariff
        && currentTariff > 0
        && Number(plan.id) < currentTariff,
    );
    const now = Date.now();
    const hasActiveSubscription = currentTariff > 0 && currentExpireAt > now;
    const isUpgradeWithRecalc = Boolean(
        isPayableTariff
        && hasActiveSubscription
        && Number(plan.id) > currentTariff,
    );
    const recalcDate = hasActiveSubscription
        ? new Date(currentExpireAt).toLocaleDateString(locale)
        : '';
    const canPay = Boolean(conf.masterUrl && accountUuid && isPayableTariff && !isEnterprise && !isDowngrade);
    const canGenerateInvoice = Boolean(accountUuid && isPayableTariff && !isEnterprise && !isDowngrade);
    const canSubmitApplication = !isDowngrade;
    const returnOrigin = accountDomain
        ? resolveAccountDomain(accountDomain).origin
        : window.location.origin;
    const payUrl = canPay
        ? `${String(conf.masterUrl).replace(/\/+$/, '')}/wfp/pay/${encodeURIComponent(accountUuid)}/${plan.id}?returnUrl=${encodeURIComponent(returnOrigin || window.location.origin)}`
        : '';
    const showPopularBadge = Boolean(plan.featured && !isCurrent);
    const showCurrentBadge = isCurrent;

    return (
        <Col xs={12} md={6} xl={4}>
            <Card
                className={[
                    'tariff-plan h-100',
                    plan.featured ? 'tariff-plan--featured' : '',
                    isCurrent ? 'tariff-plan--current' : '',
                ].filter(Boolean).join(' ')}
            >
                {showPopularBadge && (
                    <Badge bg="primary" className="tariff-plan__badge tariff-plan__badge--popular">
                        {t('popular')}
                    </Badge>
                )}
                {showCurrentBadge && (
                    <Badge bg="success" className="tariff-plan__badge tariff-plan__badge--current">
                        {t('currentPlan')}
                    </Badge>
                )}

                <Card.Body className="tariff-plan__body">
                    <div
                        className={[
                            'tariff-plan__icon',
                            plan.featured ? 'bg-primary text-white' : 'bg-light-primary text-primary',
                        ].join(' ')}
                        aria-hidden="true"
                    >
                        <SolarIcon name={PLAN_ICONS[plan.key]} />
                    </div>

                    <h5 className="tariff-plan__name mb-1 fw-bold">{t(`plans.${plan.key}.name`)}</h5>
                    <p className="tariff-plan__desc text-muted mb-3">{t(`plans.${plan.key}.description`)}</p>

                    <div className="tariff-plan__price-wrap">
                        <p className="tariff-plan__price mb-0 fw-bold">
                            {formattedPrice}
                            {isPl ? (
                                <span className="fs-6 fw-normal text-muted ms-2">{t('pricePlusVat')}</span>
                            ) : null}
                        </p>
                        <p className="text-muted f-12 mt-1 mb-0">
                            {plan.pricePeriod === 'purchase' ? t('ownServerPurchase') : t('perYear')}
                        </p>
                    </div>

                    <ul className="tariff-plan__features">
                        <li className="tariff-plan__feature">
                            <SolarIcon name="box-seam" className="text-primary" aria-hidden="true" />
                            <span>
                                <strong className="fw-medium">{t('objects')}:</strong>{' '}
                                {isEnterprise ? t('unlimited') : t('upToObjects', { count: plan.maxObjects })}
                            </span>
                        </li>
                        <li className="tariff-plan__feature">
                            <SolarIcon name="hdd-stack" className="text-primary" aria-hidden="true" />
                            <span>
                                <strong className="fw-medium">{t('storage')}:</strong>{' '}
                                {isEnterprise ? t('unlimited') : t('upToStorage', { count: plan.maxStorageGb })}
                            </span>
                        </li>
                    </ul>

                    <div className="tariff-plan__action">
                        {!isEnterprise && !isPl && (
                            <Form.Check
                                type="switch"
                                id={`tariff-by-details-${plan.key}`}
                                className="mb-3"
                                checked={invoiceByDetailsEnabled}
                                onChange={(e) => onInvoiceByDetailsToggle(e.target.checked)}
                                label={t('payByDetailsSwitch')}
                            />
                        )}
                        <Button
                            variant={plan.featured ? 'primary' : 'outline-primary'}
                            className="w-100"
                            disabled={isPl
                                ? !canSubmitApplication
                                : (!isEnterprise && !(invoiceByDetailsEnabled ? canGenerateInvoice : canPay))}
                            onClick={() => {
                                if (isPl) {
                                    onApplicationRequest?.(plan);
                                    return;
                                }
                                if (isEnterprise) {
                                    onEnterpriseRequest?.();
                                    return;
                                }
                                if (invoiceByDetailsEnabled) {
                                    onInvoiceRequest?.(plan);
                                    return;
                                }
                                if (payUrl) window.location.href = payUrl;
                            }}
                        >
                            {isEnterprise ? t('applyRequest') : (isCurrent ? t('currentPlan') : t('selectPlan'))}
                        </Button>
                        {isCurrent && hasActiveSubscription && !isPl && (
                            <p className="text-muted f-12 mt-2 mb-0">
                                {t('renewCurrentPlanHint')}
                            </p>
                        )}
                        {!isCurrent && isUpgradeWithRecalc && !isPl && (
                            <p className="text-muted f-12 mt-2 mb-0">
                                {t('upgradeRecalcHint', { date: recalcDate })}
                            </p>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );
};

export default TariffItem;
